import { Check } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  IconButton,
  SxProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface SaveButtonProps {
  onClick?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  label?: string;
  loadingLabel?: string;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
  sx?: SxProps;
}

export function SaveButton({
  onClick,
  disabled,
  isLoading,
  label,
  loadingLabel,
  color,
  sx = {},
}: SaveButtonProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <IconButton
        onClick={onClick}
        disabled={disabled || isLoading}
        type="submit"
        role="submit"
        data-testid="global-save-button"
        sx={{
          mt: theme => theme.spacing(2),
          position: 'fixed',
          top: -7,
          zIndex: 9999,
          right: theme => theme.spacing(9),
          background: theme => theme.palette.primary.main,
          ...sx,
        }}
      >
        {isLoading ? <CircularProgress /> : <Check sx={{ color: 'white' }} />}
      </IconButton>
    );
  }

  return (
    <Button
      color={color ?? 'primary'}
      variant="contained"
      type="submit"
      role="submit"
      disabled={disabled || isLoading}
      onClick={onClick}
      data-testid="global-save-button"
      sx={{
        mt: theme => theme.spacing(2),
        position: 'fixed',
        top: -4,
        zIndex: 9999,
        right: theme => theme.spacing(8),
        ...sx,
      }}
    >
      {isLoading
        ? loadingLabel ?? t('general.isSaving')
        : label ?? t('general.save')}
    </Button>
  );
}
